import { BaseBreadcrumbs }                                 from '../ui/components/BaseBreadcrumbs'
import React, { useState }                                 from 'react'
import {
  Checkbox,
  Container, FormControlLabel, Stack,
  TableBody, TableCell, TableFooter, TableHead, TableRow,
}                                                          from '@mui/material'
import { useTranslation }                                  from 'react-i18next'
import Page                                                from '../ui/components/Page'
import { useApi }                                          from '../utils/api'
import { fExportPrice, fOperator, fOperatorClass, fPrice } from '../utils/textutils'
import CommonStatisticsPage                                from '../common/CommonStatisticsPage'
import styled                                              from '@emotion/styled'

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&.OperatorRow .MuiTableCell-root': {
    color: '#ffffff',
  },
  '&.OperatorRow.Orange': {
    backgroundColor: '#f16e00',
  },
  '&.OperatorRow.Telekom': {
    backgroundColor: '#e20074',
  },
  '&.OperatorRow.O2': {
    backgroundColor: '#0090d0',
  },
  '&.OperatorRow.Swan': {
    backgroundColor: '#fad210',
    '& .MuiTableCell-root': {
      color: '#000000',
    }
  },
}));

const highlightOperatorKey = 'highlightOperator'
const SharesStatisticsPage = () => {

  const api = useApi()
  const { t } = useTranslation()
  const [highlightOperator, setHighlightOperator] = useState(localStorage.getItem(highlightOperatorKey) === 'true')

  const setHighlightOperatorImpl = (e) => {
    localStorage.setItem(highlightOperatorKey, e.target.checked)
    setHighlightOperator(e.target.checked)
  }

  return (
    <Page title={t('Shares statistics')}>
      <Container component="main">
        <BaseBreadcrumbs key={'breadcrumbs-shares'}
                         title={t('Shares statistics')}
                         items={[ ]}/>
        <CommonStatisticsPage
          fnApiLoadData={api.transactionShare.statistics}
          pathPrefix={'/statistics/shares'}
          defaultGroupBy={['service', 'type']}
          allowedGroupBy={['service', 'partner', 'operator', 'type', 'accounted', 'keyword', 'validAnswer']}
          exportFileName={'shares'}
          exportRow={(row, filter) => {
            let obj = {}
            if (filter.groupBy.includes('service')) {
              obj[t('Service')] = row.service.title ?? ''
            }
            if (filter.groupBy.includes('partner')) {
              obj[t('Partner')] = row.partner?.name ?? ''
            }
            if (filter.groupBy.includes('operator')) {
              obj[t('Operator')] = fOperator(row.operator?.name)
            }
            if (filter.groupBy.includes('type')) {
              obj[t('type')] = t(`transactionType_${row.type}`)
            }
            if (filter.groupBy.includes('accounted')) {
              obj[t('Accounted')] = row.accounted
            }
            if (filter.groupBy.includes('keyword')) {
              obj[t('Keyword')] = row.keyword
            }
            if (filter.groupBy.includes('validAnswer')) {
              obj[t('Valid answer')] = row.validAnswer
            }
            obj[t('Messages count')] = row.messagesCount ?? 0
            obj[t('Count')] = row.count ?? 0
            obj[t('Price')] = fExportPrice(row.sumPrice ?? 0)
            return obj
          }}
          customOptions={
            <Stack key={'stack-option'} alignItems={'end'}>
              <FormControlLabel control={<Checkbox checked={highlightOperator} onChange={setHighlightOperatorImpl} />} label={t('Highlight operator')} />
            </Stack>
          }
        >
          <StatsTableHeader />
          <StatsTableBody highlightOperator={highlightOperator} />
          <StatsTableFooter />
        </CommonStatisticsPage>
      </Container>
    </Page>)
}

const StatsTableHeader = ({ t, filter, data }) => {

  return <TableHead>
    <TableRow>
      {filter.groupBy.includes('service') && <TableCell>{t('Service')}</TableCell>}
      {filter.groupBy.includes('partner') && <TableCell>{t('Partner')}</TableCell>}
      {filter.groupBy.includes('operator') && <TableCell>{t('Operator')}</TableCell>}
      {filter.groupBy.includes('type') && <TableCell>{t('Type')}</TableCell>}
      {filter.groupBy.includes('accounted') && <TableCell>{t('Accounted')}</TableCell>}
      {filter.groupBy.includes('keyword') && <TableCell>{t('Keyword')}</TableCell>}
      {filter.groupBy.includes('validAnswer') && <TableCell>{t('Valid answer')}</TableCell>}
      <TableCell align={'right'}>{t('Messages count')}</TableCell>
      <TableCell align={'right'}>{t('Count')}</TableCell>
      <TableCell align={'right'}>{t('Price')}&nbsp;€</TableCell>
    </TableRow>
  </TableHead>
}

const StatsTableBody = ({ t, filter, data, highlightOperator }) => {

  return <TableBody>
    {data.map((row, i) => <StyledTableRow className={highlightOperator ? `OperatorRow ${fOperatorClass(row.operator?.code)}` : ''} key={`row-${i}`}>
      {filter.groupBy.includes('service') && <TableCell>{row.service.title ?? ''}</TableCell>}
      {filter.groupBy.includes('partner') && <TableCell>{row.partner?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('operator') && <TableCell>{row.operator?.name ?? ''}</TableCell>}
      {filter.groupBy.includes('type') && <TableCell>{row.type ? t(`transactionType_${row.type}`) : ''}</TableCell>}
      {filter.groupBy.includes('accounted') && <TableCell>{row.accounted ?? ''}</TableCell>}
      {filter.groupBy.includes('keyword') && <TableCell>{row.keyword ?? ''}</TableCell>}
      {filter.groupBy.includes('validAnswer') && <TableCell>{row.validAnswer ?? ''}</TableCell>}
      <TableCell align={'right'}>{row.messagesCount ?? 0}</TableCell>
      <TableCell align={'right'}>{row.count ?? 0}</TableCell>
      <TableCell align={'right'}>{fPrice(row.sumPrice ?? 0)}</TableCell>
    </StyledTableRow>)}
  </TableBody>
}

const StatsTableFooter = ({ t, filter, data }) => {

  const sum = (prop) => data.map((row) => row[prop] ?? 0).reduce((acc, curr) => acc+curr, 0)
  const countOfEmptyCells = ['service', 'partner', 'operator', 'type', 'accounted', 'keyword', 'validAnswer'].map(groupBy => filter.groupBy.includes(groupBy)).filter(it => it).length - 1
  return <TableFooter>
    <TableRow>
      <TableCell component={'th'} scope="row">{t('Total')}</TableCell>
      {Array(countOfEmptyCells + 1 /* messagesCount */).fill().map((_, i) => <TableCell key={`cell-${i}`}>&nbsp;</TableCell>)}
      <TableCell align={'right'}>{sum('count')}</TableCell>
      <TableCell align={'right'}>{fPrice(sum('sumPrice'))}</TableCell>
    </TableRow>
  </TableFooter>
}

export default SharesStatisticsPage